/**
 * 1_CypAdhdSemiStructuredInterview.js — CYP ADHD Semi-Structured Interview.
 *
 * One page for the whole interview, so the notes and the place in the
 * visit stay put while the clinician moves between stages. The address bar
 * still names the stage (symptoms, domains, review) so a link can open
 * that stage directly:
 *
 *   0. Clinical Observation           only when cypAdhdFlow.mse ===
 *                                     'observationInSsi' (no live org stamps
 *                                     this; TOC matches WLC and keeps the
 *                                     MSE in the Clinical Interview). A few
 *                                     light activities with the child, notes
 *                                     tagged to the DSM symptoms. The phase
 *                                     stays so an org document can turn it
 *                                     back on.
 *   1. Inattention                    DSM-5 symptoms 1-9
 *   2. Hyperactivity and impulsivity  DSM-5 symptoms 10-18
 *   3. Domains of Functional Impairment   the three life-area domains
 *   4. Review and synthesis           emerging differentials + review notes
 *
 * The interview is conducted with the child AND their parent/carer together,
 * so every notes box is source-tagged (parent/carer, child, school) and the
 * attribution survives into the report prompts. The observation is the
 * exception: it is the clinician watching the child, so its notes carry
 * symptom tags instead (config/cypAdhdObservationConfig.js), and a note
 * tagged A1c is shown again on the Listening symptom page.
 *
 * Storage — assessmentStages/semiStructuredInterview. THE contract is
 * python-functions/constants/cyp_adhd/ssi_data_model.py; this page writes
 * exactly that shape and the seeder pre-creates it:
 *
 *   data.symptoms.{1..18}.{persistedSixMonths, presentBeforeTwelve,
 *     presentAfterTwelve, settings.{home,school}, clinicianNotes,
 *     environments.{home,education}.{met, selectedExamples, examplePeriods,
 *     otherText, notes}}
 *     — the determinations hold true / false / 'inconclusive'; a Home or
 *     Education chip writes the Criterion C setting and mirrors it to `met`.
 *     otherText, notes, and the Current / Primary school ticks are no longer
 *     collected. Unticking an example still clears any periods stored earlier.
 *   data.functionalImpairment.{education,social,psychological}
 *     .{isImpairedSelection, selectedExamples, clinicianNotes}
 *     — isImpairedSelection is the "at least moderate impairment" Yes/No.
 *     data.functionalImpairmentLevel may still exist on older interviews; the
 *     page no longer asks for one level across all domains.
 *   data.emergingDifferentials = [{ id, label }]   <- consumed by the DD's
 *     Other Conditions step, which keys its outcomes by these ids
 *   data.emergingFormulation = structured-notes array   <- neither reaches the
 *   data.confidentialNotes   = structured-notes array      patient report
 *   data.observation.tasks.{taskId}.{notAdministered, promptsDone,
 *     masterNotes}   <- observationInSsi only; not seeded, created on the
 *                       first edit; not yet read by any report pipeline.
 *                       No live org stamps this. A document without the key
 *                       is "no observation recorded".
 *
 * Age-aware onset question: "present before age 12" is asked from age 13
 * only. A child of 12 or under is inside the age-12 window already, so the
 * answer is implied and asking it invites a meaningless negative.
 *
 * The upper bound is deliberately OPEN, not 16. Sai's spec said "13-16";
 * Eve confirmed 13-and-over (25 Aug) — 17 is still useful, and 18 is the
 * point at which a young person moves to the adult service. The pathway
 * accepts 5-17, so capping at 16 would silently cost a 17-year-old their
 * Criterion B evidence.
 */

import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import Layout from '../../../components/Layout';
import Breadcrumb from '../../../components/Breadcrumb';
import { buildCypSnapEvidenceSources } from '../../../utils/cypSnapEvidence';
import PreviewModeBanner from '../../../components/PreviewModeBanner';
import { useIsPreviewMode } from '../../../hooks/useIsPreviewMode';
import { usePreviewAwareNavigate } from '../../../hooks/usePreviewAwareNavigate';
import ModularStepFooter from '../../../components/autismModular/ModularStepFooter';
import ModuleCompletionDialog, {
  CancelButton,
  GenerateReportButton,
  SummarySection,
  DialogMessage,
} from '../../../components/ModuleCompletionDialog';
import GenerateReportProgressDialog from '../../../components/GenerateReportProgressDialog';
import CypAdhdSsiSideNavbar from './components/CypAdhdSsiSideNavbar';
import CypAdhdSsiSymptomPanel from './components/CypAdhdSsiSymptomPanel';
import CypAdhdSsiDomainPanel from './components/CypAdhdSsiDomainPanel';
import CypAdhdSsiDecisionTables from './components/CypAdhdSsiDecisionTables';
import CypAdhdDecisionSummaryDialog from './components/CypAdhdDecisionSummaryDialog';
import CypAdhdObservationTaskPanel from './components/CypAdhdObservationTaskPanel';
import StructuredNotesEditor from '../12_observation/components/StructuredNotesEditor';
import ConditionSelectionPanelTOC from '../6_clinicalInterview/components/ConditionSelectionPanelTOC';
import { CYP_NOTE_TAGGING_ENABLED, CYP_NOTE_TAG_OPTIONS } from '../../../constants/cypAdhdNoteTagOptions';
import { AccordionData } from '../../../constants/data_DF';
import { ADHD_CONDITION_LABEL } from '../../../utils/diagnosticDecisionRouting';
import { ModularPatientDataContext } from '../../../context/ModularPatientDataContext';
import { useAuth } from '../../../hooks/useAuth';
import { useCanEditStage } from '../../../hooks/useCanEditStage';
import useWorkflowConfig from '../../../hooks/useWorkflowConfig';
import { useModularSectionExitRegeneration } from '../../../hooks/useModularSectionExitRegeneration';
import { TriggerGenerateDocumentSection } from '../../../utils/TriggerGenerateDocumentSection';
import { recordInferredAppointmentDateOnCompletion } from '../../../utils/appointmentDateHelpers';
import { normaliseNotesValue } from '../../../utils/autismReviewData';
import { moveNoteBetweenArrays } from '../../../utils/structuredNotesMove';
import {
  CYP_SSI_ENVIRONMENTS,
  CYP_SSI_IMPAIRMENT_DOMAINS,
  CYP_SSI_SYMPTOMS,
  cypSsiSymptomNavPrefix,
  cypSsiSummaryColumns,
  buildCypSsiDecisionRows,
  differentialId,
  isCypSsiDetermined,
  ageFromDateOfBirth,
  CYP_ONSET_QUESTION_MIN_AGE as ONSET_QUESTION_MIN_AGE,
  CYP_AFTER_TWELVE_MIN_AGE as AFTER_TWELVE_MIN_AGE,
} from './config/cypAdhdSsiConfig';

// Re-exported so existing callers keep the names this page has always used.
export { ageFromDateOfBirth, ONSET_QUESTION_MIN_AGE, AFTER_TWELVE_MIN_AGE };
import {
  CYP_ADHD_OBSERVATION_INTRO,
  CYP_ADHD_OBSERVATION_TASKS,
  CYP_ADHD_OBSERVATION_TITLE,
  collectCypObservationTaggedNotes,
  isCypObservationTaskComplete,
} from './config/cypAdhdObservationConfig';

const STAGE_KEY = 'semiStructuredInterview';

// Three phases, matching the adult SSI's three pages (Symptoms → Domains →
// Review). Within the first two the clinician walks ONE item at a time with the
// side navbar showing where they are, rather than scrolling a stack of nine —
// again as on adult, where a screen holds one symptom and its evidence.
//
// Orgs stamped cypAdhdFlow.mse === 'observationInSsi' get a fourth phase
// IN FRONT: the Clinical Observation with the child. No live org stamps
// this (TOC uses the CI's Clinical Observation MSE, matching WLC); the
// phase stays so the org document can turn it back on. The page builds
// its live phase list (stepIds, below) from this base plus the stamp, so
// everything that walks phases — progress, the footer, the URL — counts four
// when the phase is on and three otherwise, without a hard-coded denominator.
const BASE_STEP_IDS = ['symptoms', 'impairment', 'review'];
const OBSERVATION_STEP_ID = 'observation';
// Address-bar names. "domains" matches the adult SSI URL; the page still
// calls that step "impairment" internally.
const STEP_SLUG = {
  observation: 'observation',
  symptoms: 'symptoms',
  impairment: 'domains',
  review: 'review',
};
const SLUG_TO_STEP = {
  observation: 'observation',
  symptoms: 'symptoms',
  domains: 'impairment',
  review: 'review',
};
const STEP_LABELS = {
  observation: CYP_ADHD_OBSERVATION_TITLE,
  symptoms: 'Symptoms',
  impairment: 'Functional Impairment',
  // Named as adult names it (3_SSIReview.js breadcrumb and heading), so the
  // sidebar, the Next button, the breadcrumb and the page heading all agree and
  // a clinician moving between pathways reads one name for one step.
  review: 'Review & Synthesis',
};

// The backend generates one paragraph per symptom under "Symptoms"
// (llm_processor_cyp_ssi.py), so the whole symptom walk is one report section.
// The observation has no entry on purpose: how its notes reach the report is
// not yet decided, so leaving the phase must not request a paragraph.
const STEP_TO_REPORT_SECTION = {
  symptoms: 'Symptoms',
  impairment: 'Functional Impairment',
};

/**
 * A symptom is complete when every Yes/No on that page has an answer.
 *
 * The six-month question is always there. "Present before age 12" is on
 * the page once the child is over 12 (and at every age when the org asks
 * the onset pair). "Present after age 12" is on the page only for that
 * half of the pair. A question that is not shown is not a missing
 * decision — the same rule Adult ADHD uses for adulthood and childhood.
 * Yes, No, and a stored Inconclusive all count. Null does not.
 * Settings are evidence of where a present symptom is seen, not another
 * forced decision.
 */
export function isCypSsiSymptomComplete(stored = {}, {
  requireBeforeTwelve = false,
  requireAfterTwelve = false,
} = {}) {
  if (!isCypSsiDetermined(stored?.persistedSixMonths)) return false;
  if (requireBeforeTwelve && !isCypSsiDetermined(stored?.presentBeforeTwelve)) return false;
  if (requireAfterTwelve && !isCypSsiDetermined(stored?.presentAfterTwelve)) return false;
  return true;
}

/**
 * Turns the Review & Synthesis notes boxes into the plain text the
 * completion popup shows. Each saved point is one line. An empty box
 * (nothing typed, or only blank points) is "not provided", same wording
 * as the adult ADHD popup.
 */
function reviewNotesSummary(notes) {
  return (Array.isArray(notes) ? notes : [])
    .map((note) => (typeof note === 'string' ? note : note?.text || ''))
    .map((text) => text.trim())
    .filter(Boolean)
    .join('\n');
}

export default function CypAdhdSemiStructuredInterview() {
  const params = useParams();
  const patientId = params.patientId;
  // The splat is '' on the bare module URL and 'symptoms' | 'domains' |
  // 'review' on a hotlink. The page stays mounted either way.
  const stepSlug = String(params['*'] || '').split('/')[0];
  const navigate = usePreviewAwareNavigate();
  const isPreviewMode = useIsPreviewMode();
  const { claims, user } = useAuth();

  const {
    header,
    assessmentStages,
    reportContent,
    informantFormInstances,
    updateAssessmentStage,
    flushStageWrites,
    error,
  } = useContext(ModularPatientDataContext);

  const canEdit = useCanEditStage(header || {}, 'SSI', { previewMode: isPreviewMode });
  const wf = useWorkflowConfig(header, claims);

  // SNAP-IV evidence beside each symptom.
  // TOC orgs show BOTH the parent/carer and school SNAP-IV responses so the
  // clinician sees all available evidence when deciding whether each symptom
  // is present. WLC requested the parent/carer response be withheld during
  // the interview so it does not anchor the symptom decisions; those scores
  // stay on the patient overview and the MDT review, which read the same
  // form instances through CypSnapScalesPanel rather than this list.
  const snapEvidenceSources = useMemo(
    () => {
      const sources = buildCypSnapEvidenceSources(header?.informants, informantFormInstances);
      return wf.cypHasObservationInSsi
        ? sources
        : sources.filter((source) => source.role !== 'legalGuardian');
    },
    [header?.informants, informantFormInstances, wf.cypHasObservationInSsi],
  );

  const ssiStage = assessmentStages?.[STAGE_KEY];
  const ssiData = ssiStage?.data || {};
  // Memoised: a fresh {} fallback on every render would change the identity of
  // every callback that depends on it, re-creating handlers each render.
  const symptomData = useMemo(() => ssiData.symptoms || {}, [ssiData.symptoms]);
  const impairmentData = useMemo(
    () => ssiData.functionalImpairment || {}, [ssiData.functionalImpairment],
  );
  // Absent for every WLC patient and for every TOC patient created before the
  // observation existed; the first edit creates the path. Readers treat a
  // missing object as "nothing recorded yet".
  const observationData = useMemo(
    () => ssiData.observation || {}, [ssiData.observation],
  );

  // The live phase list: the observation in front for TOC (see BASE_STEP_IDS).
  const hasObservation = Boolean(wf.cypHasObservationInSsi);
  const stepIds = useMemo(
    () => (hasObservation ? [OBSERVATION_STEP_ID, ...BASE_STEP_IDS] : BASE_STEP_IDS),
    [hasObservation],
  );

  const childName = header?.firstName || 'this child';
  // Onset capture per the org's stamp (TDI-205). The same helper drives the
  // MDT Review tables, so the columns there match the questions asked here.
  const { onsetAsked: asksOnsetQuestion, afterTwelveAsked: asksAfterTwelveQuestion } =
    cypSsiSummaryColumns({
      dateOfBirth: header?.dateOfBirth,
      useOnsetPair: wf.cypUseOnsetPair,
    });

  const [activeStepId, setActiveStepId] = useState(
    () => SLUG_TO_STEP[stepSlug] || 'symptoms',
  );
  // The slug this page last wrote. A later URL change that does not match
  // it came from the address bar (a hotlink, or the browser Back button).
  const slugWeSet = useRef(stepSlug);
  // Cursors within the walked phases. Kept per-phase so returning to the
  // symptoms from the domains lands where the clinician left off.
  const [observationTaskIndex, setObservationTaskIndex] = useState(0);
  const [symptomIndex, setSymptomIndex] = useState(0);
  const [domainIndex, setDomainIndex] = useState(0);

  // The first phase depends on the org's stamp, which is read from the
  // patient header — loaded AFTER the activeStepId initialiser above ran
  // against the URL alone. Two corrections, both confined to the case where
  // the address bar does not name a phase the clinician chose:
  //   - a phase this org does not have (a WLC hotlink to /observation, or a
  //     TOC child stamped before the observation existed) falls back to the
  //     first phase the org does have;
  //   - the bare module URL lands on the FIRST phase, which for TOC is the
  //     observation rather than symptoms. slugWeSet is still '' there: every
  //     in-page move writes a slug, so '' means the clinician has not moved.
  useEffect(() => {
    if (!stepIds.includes(activeStepId)) {
      setActiveStepId(stepIds[0]);
      return;
    }
    if (stepSlug === '' && slugWeSet.current === '' && activeStepId !== stepIds[0]) {
      setActiveStepId(stepIds[0]);
    }
  }, [stepIds, activeStepId, stepSlug]);

  const [hasChanges, setHasChanges] = useState(false);
  // Completion is a two-step confirm, as on every adult module.
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  // True while the simulated "generating the report" dialog is up. The
  // completion write has already happened; this only holds the clinician
  // on the page until they continue to documents, matching adult ADHD.
  const [isGenerating, setIsGenerating] = useState(false);

  const withStatusPromotion = useCallback((updates) => {
    if (!ssiStage?.status || ssiStage.status === 'notStarted') {
      return { ...updates, status: 'inProgress' };
    }
    return updates;
  }, [ssiStage?.status]);

  const persist = useCallback((dotPathUpdates) => {
    if (!canEdit) return;
    setHasChanges(true);
    updateAssessmentStage(STAGE_KEY, withStatusPromotion(dotPathUpdates));
  }, [canEdit, updateAssessmentStage, withStatusPromotion]);

  // ── Symptom edits ─────────────────────────────────────────────────────
  // There is no generic "symptom present" determination any more (26 Aug), so
  // there is nothing to clear follow-ups FROM: the two temporal answers ARE
  // the determination, mirroring the adult panel's one-question-per-life-period
  // model. The clearing logic that used to guard against a "Not present"
  // record holding stale persistence, onset and settings answers is therefore
  // gone along with the contradiction it guarded against.
  const setSymptomField = useCallback((number, field, value) => {
    persist({ [`data.symptoms.${number}.${field}`]: value });
  }, [persist]);

  // ── Settings chips ────────────────────────────────────────────────────
  // Home and Education are separate ticks, like the adult settings chips:
  // ticking one never clears the other. A second click on the same chip
  // turns it off. The Criterion C setting and the environment's `met`
  // field are written together, so the report cannot disagree with the
  // chip on screen. A record that only has the older "Met?" answer still
  // counts as on until the chip is clicked.
  const toggleSetting = useCallback((number, environmentKey) => {
    const environment = CYP_SSI_ENVIRONMENTS.find((env) => env.key === environmentKey);
    if (!environment) return;
    const stored = symptomData[String(number)] || {};
    const explicit = (stored.settings || {})[environment.settingKey];
    const currently = explicit === true || explicit === false
      ? explicit === true
      : (stored.environments || {})[environmentKey]?.met === true;
    const next = !currently;
    persist({
      [`data.symptoms.${number}.settings.${environment.settingKey}`]: next,
      [`data.symptoms.${number}.environments.${environmentKey}.met`]: next,
    });
  }, [persist, symptomData]);

  const environmentFor = useCallback((number, environmentKey) => (
    (symptomData[String(number)]?.environments || {})[environmentKey] || {}
  ), [symptomData]);

  // Unticking an example also drops any Current / Primary school ticks stored
  // before those checkboxes were removed, so a period is never left against
  // an example that is no longer ticked.
  const toggleEnvironmentExample = useCallback((number, environmentKey, example) => {
    const stored = environmentFor(number, environmentKey);
    const current = stored.selectedExamples || [];
    const isTicked = current.includes(example);
    const base = `data.symptoms.${number}.environments.${environmentKey}`;
    if (isTicked) {
      persist({
        [`${base}.selectedExamples`]: current.filter((e) => e !== example),
        [`${base}.examplePeriods`]: (stored.examplePeriods || [])
          .filter((entry) => entry.example !== example),
      });
      return;
    }
    persist({ [`${base}.selectedExamples`]: [...current, example] });
  }, [persist, environmentFor]);

  // Same shape as the symptom checklists, stored per impairment domain.
  const toggleDomainExample = useCallback((domainKey, example) => {
    const current = impairmentData[domainKey]?.selectedExamples || [];
    const next = current.includes(example)
      ? current.filter((e) => e !== example)
      : [...current, example];
    persist({ [`data.functionalImpairment.${domainKey}.selectedExamples`]: next });
  }, [persist, impairmentData]);

  // Notes are held locally while typing so the editor stays responsive, and
  // written through persist on every change (the context debounces).
  const [localNotes, setLocalNotes] = useState({});
  const notesFor = useCallback((path, stored) => (
    localNotes[path] ?? normaliseNotesValue(stored)
  ), [localNotes]);

  const persistNotes = useCallback((path, dotPath, notes) => {
    setLocalNotes((prev) => ({ ...prev, [path]: notes }));
    persist({ [dotPath]: notes });
  }, [persist]);

  // ── Moving a note between boxes ───────────────────────────────────────
  // Every notes box on this module, addressed by the same `path` id the
  // editors already use. A note written under the wrong symptom is a routine
  // mistake across an eighteen-symptom walk, and without this the only remedy
  // is to retype it — so the catalogue covers all three groups (symptoms,
  // domains, review) and the picker moves a note across pages, exactly as
  // adult autism's does across its history subsections.
  //
  // The observation tasks are in the catalogue too (so one read/move path
  // serves them), but their Move picker and everyone else's are kept APART
  // — see observationMoveTargets below.
  const notesBoxes = useMemo(() => {
    const boxes = CYP_ADHD_OBSERVATION_TASKS.map((task) => ({
      id: `obs-${task.id}`,
      label: task.shortTitle,
      group: CYP_ADHD_OBSERVATION_TITLE,
      dotPath: `data.observation.tasks.${task.id}.masterNotes`,
    }));
    CYP_SSI_SYMPTOMS.forEach((symptom) => boxes.push({
      id: `symptom-${symptom.number}`,
      label: `${symptom.dsmCode} ${symptom.name}`,
      group: symptom.group === 'inattention'
        ? 'Inattention (A1)'
        : 'Hyperactivity and impulsivity (A2)',
      dotPath: `data.symptoms.${symptom.number}.clinicianNotes`,
    }));
    CYP_SSI_IMPAIRMENT_DOMAINS.forEach((domain) => {
      boxes.push({
        id: `domain-${domain.key}`,
        label: domain.title,
        group: 'Functional Impairment',
        dotPath: `data.functionalImpairment.${domain.key}.clinicianNotes`,
      });
    });
    boxes.push({
      id: 'emergingFormulation',
      label: 'Emerging Formulation',
      group: 'Review',
      dotPath: 'data.emergingFormulation',
    });
    boxes.push({
      id: 'confidentialNotes',
      label: 'Confidential Notes',
      group: 'Review',
      dotPath: 'data.confidentialNotes',
    });
    return boxes;
  }, []);

  // Two Move catalogues that do not overlap. An observation note is the
  // clinician's account of watching the child and carries symptom TAGS; a
  // symptom-page note is the parent's or school's account and carries SOURCE
  // tags. Moving one into the other's box would misstate who said it, and
  // the tags would mean nothing where they landed. The link between the two
  // is the tag, not a move: tag an observation note A1c and it appears on
  // the Listening page by itself.
  const isObservationBox = (box) => box.id.startsWith('obs-');
  const moveTargets = useMemo(
    () => notesBoxes
      .filter((box) => !isObservationBox(box))
      .map(({ id, label, group }) => ({ id, label, group })),
    [notesBoxes],
  );
  const observationMoveTargets = useMemo(
    () => notesBoxes
      .filter(isObservationBox)
      .map(({ id, label, group }) => ({ id, label, group })),
    [notesBoxes],
  );

  /**
   * The stored notes behind one box id — the same local-then-stored read the
   * editors use, so a move never drops a note typed moments earlier that the
   * context has not yet flushed.
   */
  const readNotesBox = useCallback((boxId) => {
    if (boxId === 'emergingFormulation') return notesFor(boxId, ssiData.emergingFormulation);
    if (boxId === 'confidentialNotes') return notesFor(boxId, ssiData.confidentialNotes);
    if (boxId.startsWith('symptom-')) {
      const number = boxId.slice('symptom-'.length);
      return notesFor(boxId, (symptomData[number] || {}).clinicianNotes);
    }
    if (boxId.startsWith('domain-')) {
      const key = boxId.slice('domain-'.length);
      return notesFor(boxId, (impairmentData[key] || {}).clinicianNotes);
    }
    if (boxId.startsWith('obs-')) {
      const taskId = boxId.slice('obs-'.length);
      return notesFor(boxId, (observationData.tasks?.[taskId] || {}).masterNotes);
    }
    return [];
  }, [notesFor, ssiData.emergingFormulation, ssiData.confidentialNotes,
    symptomData, impairmentData, observationData]);

  const handleMoveNote = useCallback((sourceId, noteId, targetId, insertAt, notePatch) => {
    if (!canEdit) return;
    if (!sourceId || !targetId || sourceId === targetId) return;

    const source = notesBoxes.find((box) => box.id === sourceId);
    const target = notesBoxes.find((box) => box.id === targetId);
    if (!source || !target) return;
    // The pickers never offer a cross-phase target, but a drag can: refuse
    // it here too so the two catalogues stay apart whichever way a note
    // arrives (see moveTargets above for why).
    if (isObservationBox(source) !== isObservationBox(target)) return;

    const result = moveNoteBetweenArrays(
      readNotesBox(sourceId), readNotesBox(targetId), noteId, insertAt, notePatch,
    );
    if (!result) return;

    // Both boxes in one write: a half-applied move would either duplicate the
    // note or lose it outright.
    setLocalNotes((prev) => ({
      ...prev, [sourceId]: result.source, [targetId]: result.target,
    }));
    persist({ [source.dotPath]: result.source, [target.dotPath]: result.target });
  }, [canEdit, notesBoxes, readNotesBox, persist]);

  // ── Emerging differentials ────────────────────────────────────────────
  // The picker deals in ICD-11 labels; the stage contract stores
  // [{ id, label }] because the DD keys its per-condition outcomes by id.
  // ADHD itself is filtered out: it is the condition being assessed, not a
  // differential (same treatment as the adult ADHD and autism reviews).
  const differentialConditions = useMemo(() => {
    const result = {};
    for (const [category, conditions] of Object.entries(AccordionData)) {
      const filtered = conditions.filter(
        (condition) => condition.icd_11_label !== ADHD_CONDITION_LABEL,
      );
      if (filtered.length > 0) result[category] = filtered;
    }
    return result;
  }, []);

  const storedDifferentials = useMemo(
    () => ssiData.emergingDifferentials || [], [ssiData.emergingDifferentials],
  );
  const selectedDifferentialLabels = useMemo(
    () => storedDifferentials.map((entry) => entry.label),
    [storedDifferentials],
  );
  const [differentialsConfirmed, setDifferentialsConfirmed] = useState(
    () => Boolean(ssiData.emergingDifferentialsConfirmed),
  );

  const toggleDifferential = useCallback((label) => {
    const exists = storedDifferentials.some((entry) => entry.label === label);
    const next = exists
      ? storedDifferentials.filter((entry) => entry.label !== label)
      : [...storedDifferentials, { id: differentialId(label), label }];
    persist({ 'data.emergingDifferentials': next });
  }, [persist, storedDifferentials]);

  const confirmDifferentials = useCallback(() => {
    setDifferentialsConfirmed(true);
    persist({ 'data.emergingDifferentialsConfirmed': true });
  }, [persist]);

  const changeDifferentials = useCallback(() => {
    setDifferentialsConfirmed(false);
    persist({ 'data.emergingDifferentialsConfirmed': false });
  }, [persist]);

  // Completion gate. The ONLY gate is the org's emerging-differentials
  // policy, mirroring the adult ADHD SSI review exactly (3_SSIReview.js):
  // for Well Life Clinic the stamp is OPTIONAL, so Complete is always
  // available. Whether the SSI should ALSO require the 18 determinations is
  // the same open clinical question as the CI's completion gate — reported on
  // ASMT-18 rather than decided here.
  const canComplete = !wf.cypRequireEmergingDifferentials
    || (differentialsConfirmed && storedDifferentials.length > 0);

  // ── AI paragraph generation on step exit ──────────────────────────────
  const activeReportSection = STEP_TO_REPORT_SECTION[activeStepId] || '';

  const { markRegenHandled, resetRegenHandled } = useModularSectionExitRegeneration({
    patientId,
    stageKey: STAGE_KEY,
    sectionTitle: activeReportSection,
    hasChanges,
    canEdit,
    reportContent,
    conditions: [],
    // Review and synthesis writes no AI paragraph, so suppress the net there.
    shouldRegenerate: Boolean(activeReportSection),
    assessmentModule: 'cypAdhd',
  });

  const triggerSsiGeneration = useCallback((stepId) => {
    if (!canEdit) return;
    const sectionTitle = STEP_TO_REPORT_SECTION[stepId];
    if (!sectionTitle) return;

    const stageReport = reportContent?.[STAGE_KEY];
    const generated = stageReport?.stageSectionsGenerated || [];
    const isGenerated = generated.includes(sectionTitle);

    if (hasChanges || !isGenerated) {
      const dataReady = flushStageWrites(STAGE_KEY);
      TriggerGenerateDocumentSection(
        patientId, 'CYP ADHD Semi-Structured Interview', sectionTitle, [], dataReady,
      );
    }
  }, [canEdit, patientId, reportContent, hasChanges, flushStageWrites]);

  const goToStep = useCallback((index) => {
    const target = stepIds[index];
    if (!target) return;

    // Re-selecting the CURRENT step must not clear the dirty flag: doing so
    // discards the edit's claim on a regeneration, so the change is saved but
    // never reaches the report paragraph (same fix as the CI and DD pages).
    markRegenHandled();
    if (target !== activeStepId) {
      triggerSsiGeneration(activeStepId);
      setHasChanges(false);
    }
    resetRegenHandled();

    setActiveStepId(target);
    const slug = STEP_SLUG[target];
    slugWeSet.current = slug;
    navigate(`/patient/${patientId}/proforma/cyp-adhd/semi-structured-interview/${slug}`);
    // Four phases for TOC, three for everyone else — the denominator follows
    // the live list rather than a constant.
    const reached = (index + 1) / stepIds.length;
    const existing = typeof ssiStage?.progress === 'number' ? ssiStage.progress : 0;
    if (canEdit && reached > existing && ssiStage?.status !== 'completed') {
      // Direct write, NOT persist(): stamping progress is navigation, not a
      // clinical edit, and must not re-dirty the step just reset above.
      updateAssessmentStage(STAGE_KEY, withStatusPromotion({ progress: reached }));
    }
  }, [stepIds, activeStepId, canEdit, markRegenHandled, resetRegenHandled,
    ssiStage?.progress, ssiStage?.status, triggerSsiGeneration,
    updateAssessmentStage, withStatusPromotion, navigate, patientId]);

  // Follow a hotlink or the Back button. In-page moves set slugWeSet first,
  // so this does not run a second time for the click that just happened.
  // A slug naming a phase this org does not have (WLC, /observation) is
  // ignored here; the correction effect above settles on the first phase.
  useEffect(() => {
    const fromUrl = SLUG_TO_STEP[stepSlug];
    if (!fromUrl || stepSlug === slugWeSet.current) return;
    if (!stepIds.includes(fromUrl)) return;
    slugWeSet.current = stepSlug;
    setActiveStepId(fromUrl);
  }, [stepSlug, stepIds]);

  const handleComplete = useCallback(() => {
    // A read-only viewer's footer action is "Back to overview": it must
    // navigate, never stall on a gate that does not apply to them.
    if (!canEdit) {
      navigate(`/patient/${patientId}/cyp-adhd`);
      return;
    }
    if (!canComplete) return;
    setIsDialogOpen(true);
  }, [canComplete, canEdit, navigate, patientId]);

  /**
   * "Generate Report" on the completion popup — the adult ADHD V2 flow.
   * The stage is marked complete and the report paragraphs start in the
   * background, then the simulated progress dialog holds the clinician
   * here until they continue to documents. Cancelling that dialog does
   * not undo the completion (adult behaves the same way).
   */
  const handleConfirmComplete = useCallback(() => {
    if (!canEdit || !canComplete) return;
    setIsDialogOpen(false);

    // The last step has no report section of its own, so flush the two that
    // do in case the clinician never navigated away from them.
    markRegenHandled();
    const stageReport = reportContent?.[STAGE_KEY];
    const generated = stageReport?.stageSectionsGenerated || [];
    const dataReady = flushStageWrites(STAGE_KEY);
    ['Symptoms', 'Functional Impairment'].forEach((sectionTitle) => {
      if (hasChanges || !generated.includes(sectionTitle)) {
        TriggerGenerateDocumentSection(
          patientId, 'CYP ADHD Semi-Structured Interview', sectionTitle, [], dataReady,
        );
      }
    });

    // firstCompletedAt is write-once: the overview uses it as the inferred
    // appointment date. lastCompletedAt refreshes on every re-completion.
    let didWriteFirstCompleted = false;
    const now = new Date().toISOString();
    const completionFields = {
      status: 'completed',
      progress: 1,
      lastCompletedAt: now,
      lastCompletedBy: user?.uid || null,
      lastCompletedByName: user?.displayName || null,
    };
    if (!ssiStage?.firstCompletedAt) {
      didWriteFirstCompleted = true;
      completionFields.firstCompletedAt = now;
      completionFields.firstCompletedBy = user?.uid || null;
      completionFields.firstCompletedByName = user?.displayName || null;
    }
    updateAssessmentStage(STAGE_KEY, completionFields);

    // Inferred appointment audit on first completion (fire-and-forget; does
    // not write appointmentDates — display-only inference stays intact).
    recordInferredAppointmentDateOnCompletion({
      patientId,
      moduleKey: 'ssi',
      didWriteFirstCompleted,
      enabled: Boolean(wf.isAppointmentDatesEnabled),
    });

    // Adult opens the progress dialog here and only leaves the page when
    // that dialog's continue button is pressed.
    setIsGenerating(true);
  }, [canComplete, canEdit, flushStageWrites, hasChanges, markRegenHandled,
    patientId, reportContent, ssiStage?.firstCompletedAt,
    updateAssessmentStage, user, wf.isAppointmentDatesEnabled]);

  const handleCancelGeneration = useCallback(() => {
    setIsGenerating(false);
  }, []);

  // Progress dialog's continue button. Adult lands on the adult documents
  // page; CYP uses its own documents route. Same action: go and review the
  // report that was just requested.
  const handleAfterGeneration = useCallback(() => {
    navigate(`/patient/${patientId}/cyp-adhd/documents`);
  }, [navigate, patientId]);

  // Same rule as the sidebar tick and the missing-decision warning: every
  // Yes/No that is on the page has an answer.
  const symptomCompleteOptions = useMemo(() => ({
    requireBeforeTwelve: asksOnsetQuestion,
    requireAfterTwelve: asksAfterTwelveQuestion,
  }), [asksOnsetQuestion, asksAfterTwelveQuestion]);

  const symptomNavItems = useMemo(() => CYP_SSI_SYMPTOMS.map((symptom) => {
    // Same short labels as the adult ADHD symptom list ("A 1", "H/I 1").
    // The name stays on the item so tests and screen readers can still find
    // the symptom; the list itself shows only the prefix.
    const prefix = cypSsiSymptomNavPrefix(symptom);
    return {
      key: String(symptom.number),
      badge: prefix,
      label: symptom.name,
      fullTitle: `${prefix}: ${symptom.description}`,
      title: symptom.description,
      // Green once every Yes/No on the page has an answer. For a child over
      // 12 that is the six-month answer and "present before age 12".
      isComplete: isCypSsiSymptomComplete(
        symptomData[String(symptom.number)],
        symptomCompleteOptions,
      ),
    };
  }), [symptomData, symptomCompleteOptions]);

  // One row per task. Green once the task holds a note; a task marked not
  // administered never goes green (isCypObservationTaskComplete).
  const observationNavItems = useMemo(() => CYP_ADHD_OBSERVATION_TASKS.map((task, index) => ({
    key: task.id,
    badge: String(index + 1),
    label: task.shortTitle,
    fullTitle: task.fullTitle,
    title: task.fullTitle,
    isComplete: isCypObservationTaskComplete(observationData.tasks?.[task.id]),
  })), [observationData]);

  const domainNavItems = useMemo(() => CYP_SSI_IMPAIRMENT_DOMAINS.map((domain, index) => ({
    key: domain.key,
    badge: String(index + 1),
    label: domain.title,
    fullTitle: domain.title,
    title: domain.title,
    isComplete: (impairmentData[domain.key] || {}).isImpairedSelection !== undefined
      && (impairmentData[domain.key] || {}).isImpairedSelection !== null,
  })), [impairmentData]);

  // Read-only decision rows for the review step's summary tables.
  // Shared with the DD's Decision step (buildCypSsiDecisionRows), so the two
  // surfaces render the same determinations from one builder.
  const decisionRows = useMemo(
    () => buildCypSsiDecisionRows(ssiData),
    [ssiData],
  );

  const breadcrumbTuples = [
    ['Patient Overview', `/patient/${patientId}/cyp-adhd`],
    ['Semi-Structured Interview', null],
  ];

  // An error state, not an endless spinner: a permission-denied or network
  // failure would otherwise render "Loading…" forever with nothing to act on.
  if (error) {
    return (
      <Layout activePage="dashboard">
        <Breadcrumb tupleList={breadcrumbTuples} />
        <div className="p-8 max-w-2xl text-left">
          <h1 className="text-lg font-semibold text-gray-900 mb-2">
            This Semi-Structured Interview could not be loaded
          </h1>
          <p className="text-sm text-gray-700 mb-4">
            Something went wrong fetching this assessment. Your work has not
            been lost. Please check your connection and try again, or go back to
            the patient overview.
          </p>
          <button
            type="button"
            onClick={() => navigate(`/patient/${patientId}/cyp-adhd`)}
            className="text-sm font-medium text-violet-700 border border-violet-300 rounded-md px-4 py-2 hover:bg-violet-50"
          >
            Back to patient overview
          </button>
        </div>
      </Layout>
    );
  }

  if (!ssiStage) {
    return (
      <Layout activePage="dashboard">
        <Breadcrumb tupleList={breadcrumbTuples} />
        <div className="p-8 text-sm text-gray-500">
          Loading the Semi-Structured Interview…
        </div>
      </Layout>
    );
  }

  // One observation task at a time (TOC only). The intro sits above the first
  // task so a clinician who has not run one reads what it is for before the
  // first prompt; it is not repeated on every task.
  const renderObservation = () => {
    const index = Math.min(observationTaskIndex, CYP_ADHD_OBSERVATION_TASKS.length - 1);
    const task = CYP_ADHD_OBSERVATION_TASKS[index];
    const taskData = observationData.tasks?.[task.id] || {};
    const notesPath = `obs-${task.id}`;

    return (
      <div className="w-full text-left">
        <CypAdhdObservationTaskPanel
          task={task}
          taskData={taskData}
          canEdit={canEdit}
          index={index}
          total={CYP_ADHD_OBSERVATION_TASKS.length}
          intro={index === 0 ? CYP_ADHD_OBSERVATION_INTRO : null}
          onFieldChange={(field, value) => persist({
            [`data.observation.tasks.${task.id}.${field}`]: value,
          })}
          notes={notesFor(notesPath, taskData.masterNotes)}
          onNotesChange={(next) => persistNotes(
            notesPath,
            `data.observation.tasks.${task.id}.masterNotes`,
            next,
          )}
          notesDomainId={notesPath}
          noteMoveTargets={observationMoveTargets}
          onMoveNote={handleMoveNote}
        />
      </div>
    );
  };

  // One symptom at a time, as on the adult SSI. The clinician moves with the
  // panel's own Previous/Next Symptom buttons or by picking from the navbar.
  const renderSymptomStep = () => {
    const index = Math.min(symptomIndex, CYP_SSI_SYMPTOMS.length - 1);
    const symptom = CYP_SSI_SYMPTOMS[index];
    const stored = symptomData[String(symptom.number)] || {};
    // What the clinician saw the child do, if they tagged it to this
    // symptom during the observation. The panel is only mounted for an
    // org that runs the phase; an empty list is the "nothing tagged"
    // line, not a reason to hide the column.
    const observationNotes = hasObservation
      ? collectCypObservationTaggedNotes(observationData, symptom.dsmCode)
      : [];

    return (
      <div className="w-full text-left">
        <CypAdhdSsiSymptomPanel
          symptom={symptom}
          stored={stored}
          canEdit={canEdit}
          observationNotes={observationNotes}
          showObservationEvidence={hasObservation}
          asksOnsetQuestion={asksOnsetQuestion}
          asksAfterTwelveQuestion={asksAfterTwelveQuestion}
          onsetPairMode={wf.cypUseOnsetPair}
          childName={childName}
          notes={notesFor(`symptom-${symptom.number}`, stored.clinicianNotes)}
          index={index}
          total={CYP_SSI_SYMPTOMS.length}
          snapEvidenceSources={snapEvidenceSources}
          onFieldChange={(field, value) => setSymptomField(symptom.number, field, value)}
          onSettingToggle={(environmentKey) => toggleSetting(symptom.number, environmentKey)}
          onToggleEnvironmentExample={(environmentKey, example) => toggleEnvironmentExample(
            symptom.number, environmentKey, example,
          )}
          onNotesChange={(next) => persistNotes(
            `symptom-${symptom.number}`,
            `data.symptoms.${symptom.number}.clinicianNotes`,
            next,
          )}
          notesDomainId={`symptom-${symptom.number}`}
          noteMoveTargets={moveTargets}
          onMoveNote={handleMoveNote}
        />
      </div>
    );
  };

  // One domain at a time, mirroring the adult Domains page. Reuses the symptom
  // panel's card chrome via CypAdhdSsiDomainCard below rather than a second
  // layout, so both walks look like one section of one platform.
  const renderImpairment = () => {
    const index = Math.min(domainIndex, CYP_SSI_IMPAIRMENT_DOMAINS.length - 1);
    const domain = CYP_SSI_IMPAIRMENT_DOMAINS[index];
    const stored = impairmentData[domain.key] || {};
    const notesPath = `domain-${domain.key}`;

    return (
      <div className="w-full text-left">
        <CypAdhdSsiDomainPanel
          domain={domain}
          stored={stored}
          canEdit={canEdit}
          notes={notesFor(notesPath, stored.clinicianNotes)}
          index={index}
          total={CYP_SSI_IMPAIRMENT_DOMAINS.length}
          onImpairedSelect={(next) => persist({
            [`data.functionalImpairment.${domain.key}.isImpairedSelection`]: next,
          })}
          selectedExamples={stored.selectedExamples || []}
          onToggleExample={(example) => toggleDomainExample(domain.key, example)}
          onNotesChange={(next) => persistNotes(
            notesPath,
            `data.functionalImpairment.${domain.key}.clinicianNotes`,
            next,
          )}
          notesDomainId={notesPath}
          noteMoveTargets={moveTargets}
          onMoveNote={handleMoveNote}
        />
      </div>
    );
  };

  const renderReview = () => (
    <div className="max-w-5xl mx-auto text-left space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 mb-1">
          Review &amp; Synthesis
        </h1>
        <p className="text-gray-500 text-sm">
          Record any other conditions you are considering. They carry forward
          to the Diagnostic Decision, where each one gets an outcome.
        </p>
      </div>

      {/* Same summary card as adult ADHD Review & Synthesis: two buttons,
          each opening its table in a dialog. Read-only — each value is
          edited on its own symptom or domain page. */}
      <div className="bg-gray-50 rounded-2xl border border-gray-200 text-left p-6 shadow-md">
        <h2 className="font-bold text-lg mb-4">
          Summary of Semi-Structured Interview Decisions
        </h2>
        <div className="flex gap-3">
          <CypAdhdDecisionSummaryDialog
            label="View Symptoms Decisions"
            title="Symptoms Summary"
            description="DSM-5 symptom decisions, with persistence, age of onset and the number of settings each symptom is seen in"
          >
            <CypAdhdSsiDecisionTables
              show="symptoms"
              inattention={decisionRows.inattention}
              hyperactivity={decisionRows.hyperactivity}
              onsetAsked={asksOnsetQuestion}
              afterTwelveAsked={asksAfterTwelveQuestion}
            />
          </CypAdhdDecisionSummaryDialog>
          <CypAdhdDecisionSummaryDialog
            label="View Impairment Decisions"
            title="Functional Impairment"
            description="Whether each domain shows at least moderate impairment"
          >
            <CypAdhdSsiDecisionTables
              show="impairment"
              domains={decisionRows.domains}
            />
          </CypAdhdDecisionSummaryDialog>
        </div>
      </div>

      {/* Read-only, and deliberately not a selection: ADHD is the condition the
          child was referred for, so it anchors the differentials below rather
          than being one of them. Mirrors the adult review's panel. */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="font-bold text-lg mb-2">Condition(s) Referred For</h3>
        <ul className="list-disc list-inside text-sm text-gray-700">
          <li>{ADHD_CONDITION_LABEL}</li>
        </ul>
      </div>

      {/* WLC differentials flow into the DD's Other Conditions step and
          ARE included in the final report → gray/default panel. TOC skips
          per-differential decisions so they are informational only → amber
          panel + "Not included in final report" footer. Same conditional
          logic the DD Review page already applies (cypSkipDifferentialsDecisions). */}
      <ConditionSelectionPanelTOC
        title="Emerging differentials"
        variant={wf.cypSkipDifferentialsDecisions ? 'non-report' : 'default'}
        density="compact"
        searchable
        showSelectedBullets
        subtitle={
          'Conditions you are actively considering as alternative explanations, '
          + 'as co-occurring, or to rule in or out at the Diagnostic Decision. '
          + 'Record your reasoning in your notes below.'
        }
        selectedOptions={selectedDifferentialLabels}
        isMultiSelect
        isConfirmed={differentialsConfirmed}
        onSelect={toggleDifferential}
        onConfirm={confirmDifferentials}
        onChangeChoice={changeDifferentials}
        conditions={differentialConditions}
        allowEmptyConfirm={!wf.cypRequireEmergingDifferentials}
        canEdit={canEdit}
      />

      {/* Two panels rather than one, matching the adult review: a formulation
          that carries forward to the Diagnostic Decision, and private
          observations that do not. Amber chrome and the exclusion footer are
          adult's, and they are the honest thing to show — llm_inputs_cyp_ssi
          reads neither field, so nothing written here reaches the family's
          report. CYP keeps StructuredNotesEditor rather than adult's
          DictationTextArea: source tagging (parent / child / school) is what
          makes a CYP note attributable, and adult has no equivalent. */}
      <div className="bg-amber-50 rounded-2xl border border-amber-200 text-left px-6 pt-6 pb-2 shadow-md flex flex-col">
        <h3 className="font-bold text-lg mb-2">Emerging Formulation</h3>
        <p className="text-sm text-gray-600 mb-4">
          Your working hypothesis about what is going on for {childName} and
          why. Capture the presenting picture, contributing factors, functional
          impact and strengths. Finalised at Diagnostic Decision.
        </p>
        <StructuredNotesEditor
          notes={notesFor('emergingFormulation', ssiData.emergingFormulation)}
          onChange={(notes) => persistNotes(
            'emergingFormulation', 'data.emergingFormulation', notes,
          )}
          domainId="emergingFormulation"
          onMoveNoteIn={handleMoveNote}
          moveTargets={moveTargets}
          currentMoveId="emergingFormulation"
          onMoveNoteTo={handleMoveNote}
          placeholder="Add a note, then press Enter to save each point"
          enableCriteriaTagging={CYP_NOTE_TAGGING_ENABLED}
          tagOptions={CYP_NOTE_TAG_OPTIONS}
          readOnly={!canEdit}
        />
        <p className="text-xs italic text-amber-900/80 text-right mt-1 mb-0">
          Not included in final report
        </p>
      </div>

      <div className="bg-amber-50 rounded-2xl border border-amber-200 text-left px-6 pt-6 pb-2 shadow-md flex flex-col">
        <h3 className="font-bold text-lg mb-2">Confidential Notes</h3>
        <p className="text-sm text-gray-600 mb-4">
          Confidential or sensitive observations for review at Diagnostic
          Decision.
        </p>
        <StructuredNotesEditor
          notes={notesFor('confidentialNotes', ssiData.confidentialNotes)}
          onChange={(notes) => persistNotes(
            'confidentialNotes', 'data.confidentialNotes', notes,
          )}
          domainId="confidentialNotes"
          onMoveNoteIn={handleMoveNote}
          moveTargets={moveTargets}
          currentMoveId="confidentialNotes"
          onMoveNoteTo={handleMoveNote}
          placeholder="Add a note, then press Enter to save each point"
          enableCriteriaTagging={CYP_NOTE_TAGGING_ENABLED}
          tagOptions={CYP_NOTE_TAG_OPTIONS}
          readOnly={!canEdit}
        />
        <p className="text-xs italic text-amber-900/80 text-right mt-1 mb-0">
          Not included in final report
        </p>
      </div>

      {!canComplete && (
        <p className="text-sm text-gray-500">
          To complete the Semi-Structured Interview, confirm at least one
          emerging differential.
        </p>
      )}
    </div>
  );

  const stepRenderers = {
    observation: renderObservation,
    symptoms: renderSymptomStep,
    impairment: renderImpairment,
    review: renderReview,
  };

  // ─── Fixed-footer navigation ────────────────────────────────────────────────
  // ONE set of controls for the whole stage, in the sticky footer the TOC
  // autism modules use (Sai, 26 Aug). It walks items within a phase and then
  // crosses into the next phase, so the clinician never has to find a different
  // control to keep going, and the primary action is always reachable without
  // scrolling past eighteen symptom cards.
  const symptomCount = CYP_SSI_SYMPTOMS.length;
  const domainCount = CYP_SSI_IMPAIRMENT_DOMAINS.length;
  const currentSymptom = Math.min(symptomIndex, symptomCount - 1);
  const currentDomain = Math.min(domainIndex, domainCount - 1);

  // ── Missing-decision check before Review & Synthesis ──────────────────
  // Adult ADHD parity (SSIDomainQuestionPanel): leaving the impairment walk
  // for the review runs a completeness check, and anything undecided opens
  // the "Clinical Decisions Required" dialog with jumps to the first missing
  // symptom or area. One deliberate copy difference: the CYP proceed-anyway
  // note says undecided answers STAY unrecorded — nothing here coerces an
  // unanswered symptom to "not present" (the bridge deliberately passes null
  // through so the MDT never sees a determination the clinician never made).
  const [isValidationDialogOpen, setIsValidationDialogOpen] = useState(false);
  const [validationResults, setValidationResults] = useState({
    missingSymptoms: [], missingDomains: [], hasAnyMissing: false,
  });

  const validateSsiDecisions = useCallback(() => {
    const results = {
      missingSymptoms: [], missingDomains: [], hasAnyMissing: false,
    };

    CYP_SSI_SYMPTOMS.forEach((symptom) => {
      const stored = symptomData[String(symptom.number)] || {};
      // The same helper that paints the sidebar, so a green symptom cannot
      // reappear in this warning, and an unfinished one cannot stay green.
      const missingPersisted = !isCypSsiDetermined(stored.persistedSixMonths);
      const missingOnset = asksOnsetQuestion
        && !isCypSsiDetermined(stored.presentBeforeTwelve);
      const missingAfterTwelve = asksAfterTwelveQuestion
        && !isCypSsiDetermined(stored.presentAfterTwelve);
      if (!isCypSsiSymptomComplete(stored, symptomCompleteOptions)) {
        results.missingSymptoms.push({
          number: symptom.number,
          dsmCode: symptom.dsmCode,
          name: symptom.name,
          missingPersisted,
          missingOnset,
          missingAfterTwelve,
        });
      }
    });

    CYP_SSI_IMPAIRMENT_DOMAINS.forEach((domain, domainIdx) => {
      const stored = impairmentData[domain.key] || {};
      if (stored.isImpairedSelection === null || stored.isImpairedSelection === undefined) {
        results.missingDomains.push({ index: domainIdx, title: domain.title });
      }
    });

    results.hasAnyMissing = results.missingSymptoms.length > 0
      || results.missingDomains.length > 0;
    return results;
  }, [symptomData, impairmentData, asksOnsetQuestion, asksAfterTwelveQuestion,
    symptomCompleteOptions]);

  const goToReviewStep = useCallback(() => {
    setIsValidationDialogOpen(false);
    goToStep(stepIds.indexOf('review'));
  }, [goToStep, stepIds]);

  const handleImpairmentNextToReview = useCallback(() => {
    // The completeness dialog exists to stop the assigned clinician reaching
    // Review & Synthesis with undecided symptoms or domains. A previewer is
    // only reading and cannot fill anything in, so blocking them would strand
    // them before the final step — let them straight through (Adult Autism
    // SSI parity: `!isPreviewMode && incompleteSymptoms.length > 0`).
    const validation = validateSsiDecisions();
    if (!isPreviewMode && validation.hasAnyMissing) {
      setValidationResults(validation);
      setIsValidationDialogOpen(true);
      return;
    }
    goToReviewStep();
  }, [isPreviewMode, validateSsiDecisions, goToReviewStep]);

  const navigateToFirstMissingSymptom = useCallback(() => {
    if (validationResults.missingSymptoms.length === 0) return;
    setIsValidationDialogOpen(false);
    setSymptomIndex(validationResults.missingSymptoms[0].number - 1);
    goToStep(stepIds.indexOf('symptoms'));
  }, [validationResults, goToStep, stepIds]);

  const navigateToFirstMissingDomain = useCallback(() => {
    setIsValidationDialogOpen(false);
    const index = validationResults.missingDomains[0]?.index ?? 0;
    setDomainIndex(index);
  }, [validationResults]);

  const footer = (() => {
    if (activeStepId === 'observation') {
      const taskCount = CYP_ADHD_OBSERVATION_TASKS.length;
      const current = Math.min(observationTaskIndex, taskCount - 1);
      const atFirst = current === 0;
      const atLast = current === taskCount - 1;
      return {
        counterText: `Task ${current + 1} of ${taskCount}`,
        showBack: !atFirst,
        onBack: () => setObservationTaskIndex(current - 1),
        nextLabel: atLast
          ? `Next: ${STEP_LABELS.symptoms}`
          : `Next: ${CYP_ADHD_OBSERVATION_TASKS[current + 1].shortTitle}`,
        onNext: () => {
          if (atLast) {
            goToStep(stepIds.indexOf('symptoms'));
            return;
          }
          setObservationTaskIndex(current + 1);
        },
        nextDisabled: false,
        // The observation never gates the interview: if the child is not
        // here, this link walks straight past it. Hidden on the last task,
        // where the primary button IS that jump.
        phaseForwardLink: atLast ? undefined : {
          label: `Go to ${STEP_LABELS.symptoms}`,
          onClick: () => goToStep(stepIds.indexOf('symptoms')),
        },
      };
    }

    if (activeStepId === 'symptoms') {
      const atFirst = currentSymptom === 0;
      const atLast = currentSymptom === symptomCount - 1;
      return {
        counterText: `Symptom ${currentSymptom + 1} of ${symptomCount}`,
        // With an observation in front, the first symptom's Back returns to
        // the last task — it undoes the previous Next, as the first domain's
        // Back returns to the last symptom. Without one, there is nothing
        // before the first symptom and the button is hidden as before.
        showBack: !atFirst || hasObservation,
        onBack: () => {
          if (atFirst) {
            setObservationTaskIndex(CYP_ADHD_OBSERVATION_TASKS.length - 1);
            goToStep(stepIds.indexOf('observation'));
            return;
          }
          setSymptomIndex(currentSymptom - 1);
        },
        nextLabel: atLast
          ? `Next: ${STEP_LABELS.impairment}`
          : `Next: ${CYP_SSI_SYMPTOMS[currentSymptom + 1].name}`,
        onNext: () => {
          if (atLast) {
            goToStep(stepIds.indexOf('impairment'));
            return;
          }
          setSymptomIndex(currentSymptom + 1);
        },
        nextDisabled: false,
        // Adult SSI parity: a subtle skip link under the primary CTA fast
        // forwards across the remaining symptoms. Hidden on the last symptom,
        // where the primary button IS that jump.
        phaseForwardLink: atLast ? undefined : {
          label: `Go to ${STEP_LABELS.impairment}`,
          onClick: () => goToStep(stepIds.indexOf('impairment')),
        },
      };
    }

    if (activeStepId === 'impairment') {
      const atFirst = currentDomain === 0;
      const atLast = currentDomain === domainCount - 1;
      return {
        counterText: `Area ${currentDomain + 1} of ${domainCount}`,
        showBack: true,
        // Areas 2 and 3 step back one domain. The first area's Back returns
        // to the last symptom, so it undoes the previous Next.
        backLabel: atFirst ? 'Back' : 'Previous Domain',
        onBack: () => {
          if (atFirst) {
            setSymptomIndex(symptomCount - 1);
            goToStep(stepIds.indexOf('symptoms'));
            return;
          }
          setDomainIndex(currentDomain - 1);
        },
        // Adult ADHD V2 SSI parity: under Previous Domain, a text link jumps
        // back to the symptom walk without stepping through every earlier
        // domain. Hidden on the first area, whose Back button already leaves
        // the domains. The symptom cursor is left where the clinician was.
        phaseBackLink: atFirst ? undefined : {
          label: 'Go to Symptoms',
          onClick: () => goToStep(stepIds.indexOf('symptoms')),
        },
        nextLabel: atLast
          ? `Next: ${STEP_LABELS.review}`
          : `Next: ${CYP_SSI_IMPAIRMENT_DOMAINS[currentDomain + 1].title}`,
        onNext: () => {
          if (atLast) {
            // Completeness check first — undecided symptoms or areas open the
            // Clinical Decisions Required dialog rather than moving on.
            handleImpairmentNextToReview();
            return;
          }
          setDomainIndex(currentDomain + 1);
        },
        nextDisabled: false,
        // Adult ADHD and Autism SSI both keep a quiet "Go to Review &
        // Synthesis" under the primary Next while the clinician is still
        // on an earlier area. The last area's primary button IS that jump,
        // so the extra link would say the same thing twice. The link runs
        // the same completeness check as that button: an undecided symptom
        // or area still opens Clinical Decisions Required.
        phaseForwardLink: atLast ? undefined : {
          label: `Go to ${STEP_LABELS.review}`,
          onClick: handleImpairmentNextToReview,
        },
      };
    }

    return {
      counterText: STEP_LABELS.review,
      showBack: true,
      onBack: () => {
        setDomainIndex(domainCount - 1);
        goToStep(stepIds.indexOf('impairment'));
      },
      onNext: handleComplete,
      // A read-only viewer cannot complete the stage, so offering
      // "Complete Semi-Structured Interview" would present an action that only
      // navigates.
      nextLabel: canEdit ? 'Complete Semi-Structured Interview' : 'Back to overview',
      nextDisabled: canEdit && !canComplete,
    };
  })();

  const formulationSummary = reviewNotesSummary(
    notesFor('emergingFormulation', ssiData.emergingFormulation),
  );
  const confidentialSummary = reviewNotesSummary(
    notesFor('confidentialNotes', ssiData.confidentialNotes),
  );

  return (
    <Layout activePage="dashboard">
      <Breadcrumb tupleList={breadcrumbTuples} />

      <div className="flex h-[calc(100vh-8rem)]">
        {/* The same list component as the two phases below it: four tasks
            read as rows of one interview, not a separate module's sidebar. */}
        {activeStepId === 'observation' && (
          <CypAdhdSsiSideNavbar
            title={CYP_ADHD_OBSERVATION_TITLE}
            mode="expanded"
            items={observationNavItems}
            activeIndex={Math.min(observationTaskIndex, CYP_ADHD_OBSERVATION_TASKS.length - 1)}
            onSelect={setObservationTaskIndex}
          />
        )}
        {activeStepId === 'symptoms' && (
          <CypAdhdSsiSideNavbar
            title="Symptoms"
            mode="compact"
            items={symptomNavItems}
            activeIndex={Math.min(symptomIndex, CYP_SSI_SYMPTOMS.length - 1)}
            onSelect={setSymptomIndex}
          />
        )}
        {activeStepId === 'impairment' && (
          <CypAdhdSsiSideNavbar
            title="Functional Impairment"
            mode="expanded"
            items={domainNavItems}
            activeIndex={Math.min(domainIndex, CYP_SSI_IMPAIRMENT_DOMAINS.length - 1)}
            onSelect={setDomainIndex}
          />
        )}

        <div className="flex-1 flex flex-col min-h-0">
          {isPreviewMode && <PreviewModeBanner />}
          <div className="flex-1 overflow-y-auto p-8">
            {stepRenderers[activeStepId]()}
          </div>

          <ModularStepFooter
            showBack={footer.showBack}
            onBack={footer.onBack}
            backLabel={footer.backLabel}
            counterText={footer.counterText}
            onNext={footer.onNext}
            nextLabel={footer.nextLabel}
            nextDisabled={footer.nextDisabled}
            phaseBackLink={footer.phaseBackLink}
            phaseForwardLink={footer.phaseForwardLink}
          />
        </div>
      </div>

      {/* Same popup as adult ADHD V2 Review & Synthesis (MDT orgs): a
          recap of what was just entered on this page, then Generate Report.
          Symptom and domain counts stay on the page's own summary card;
          they are not repeated here. */}
      <ModuleCompletionDialog
        open={isDialogOpen && !isGenerating}
        onOpenChange={setIsDialogOpen}
        title="Semi-Structured Interview Complete"
        description={
          <>Your <span className="font-semibold">Clinical Interview</span> and <span className="font-semibold">Semi-Structured Interview</span> reports are ready for review.</>
        }
        actions={
          <>
            <CancelButton onClick={() => setIsDialogOpen(false)} />
            <GenerateReportButton onClick={handleConfirmComplete} />
          </>
        }
      >
        <SummarySection title="Condition referred for:">
          <p className="text-sm text-gray-700">{ADHD_CONDITION_LABEL}</p>
        </SummarySection>

        <SummarySection title="Emerging differentials under consideration:">
          {selectedDifferentialLabels.length === 0 ? (
            <p className="text-sm italic text-gray-400">No conditions selected</p>
          ) : (
            selectedDifferentialLabels.map((label) => (
              <div key={label} className="mb-1">• {label}</div>
            ))
          )}
        </SummarySection>

        <SummarySection title="Emerging formulation:">
          {formulationSummary ? (
            <div className="text-gray-700 text-sm whitespace-pre-wrap max-h-32 overflow-y-auto bg-gray-50 rounded-lg p-3 border border-gray-200">
              {formulationSummary}
            </div>
          ) : (
            <p className="text-sm italic text-gray-400">Not provided</p>
          )}
        </SummarySection>

        <SummarySection title="Confidential notes:">
          {confidentialSummary ? (
            <div className="text-gray-700 text-sm whitespace-pre-wrap max-h-32 overflow-y-auto bg-gray-50 rounded-lg p-3 border border-gray-200">
              {confidentialSummary}
            </div>
          ) : (
            <p className="text-sm italic text-gray-400">Not provided</p>
          )}
        </SummarySection>

        <DialogMessage>
          Your formulation notes and emerging differentials will be available as a reference during the MDT review.
        </DialogMessage>
      </ModuleCompletionDialog>

      <GenerateReportProgressDialog
        reportStage="Semi-Structured Interview"
        patientId={patientId}
        clinicianId={user?.uid}
        handleNextPageButtonClick={handleAfterGeneration}
        isGenerating={isGenerating}
        onCancel={handleCancelGeneration}
      />

      {/* Missing-decision dialog — adult ADHD parity (SSIDomainQuestionPanel's
          "Clinical Decisions Required"), fired when leaving the impairment
          walk for Review & Synthesis with anything undecided. */}
      <AlertDialog.Root open={isValidationDialogOpen} onOpenChange={setIsValidationDialogOpen}>
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/50" />
          <AlertDialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-6 w-[600px] max-w-[90vw] max-h-[85vh] overflow-y-auto text-left">
            <div className="flex justify-between items-start mb-4">
              <AlertDialog.Title className="text-xl font-bold text-gray-900">
                Clinical Decisions Required
              </AlertDialog.Title>
              <AlertDialog.Cancel asChild>
                <button className="absolute top-2 right-3 text-gray-500 hover:text-gray-700" aria-label="Close">
                  <span aria-hidden>×</span>
                </button>
              </AlertDialog.Cancel>
            </div>

            <AlertDialog.Description className="text-sm text-gray-600 mb-4">
              Some clinical decisions have not been made yet. Please review the
              missing decisions below.
            </AlertDialog.Description>

            <div className="mb-6">
              {validationResults.missingSymptoms.length > 0 && (
                <div className="mb-4">
                  <h3 className="font-semibold mb-2 text-gray-700">
                    {validationResults.missingSymptoms.length} Clinical Decision{validationResults.missingSymptoms.length === 1 ? '' : 's'} for Symptoms {validationResults.missingSymptoms.length === 1 ? 'is' : 'are'} missing:
                  </h3>
                  <div className="bg-red-50 rounded-lg p-3 max-h-32 overflow-y-auto">
                    {validationResults.missingSymptoms.slice(0, 4).map((symptom) => (
                      <div key={symptom.number} className="text-sm text-red-800 mb-1">
                        <span className="font-medium">{symptom.dsmCode}:</span>{' '}
                        {[
                          (symptom.missingPersisted || symptom.missingOnset
                            || symptom.missingAfterTwelve)
                            && `Decision for ${[
                              symptom.missingPersisted && 'persisted for at least 6 months',
                              symptom.missingOnset && 'present before age 12',
                              symptom.missingAfterTwelve && 'present after age 12',
                            ].filter(Boolean).join(' and ')}`,
                        ].filter(Boolean).join('; ')}
                      </div>
                    ))}
                    {validationResults.missingSymptoms.length > 4 && (
                      <div className="text-sm text-red-700 font-medium">
                        ... and {validationResults.missingSymptoms.length - 4} other missing
                      </div>
                    )}
                  </div>
                </div>
              )}

              {validationResults.missingDomains.length > 0 && (
                <div className="mb-4">
                  <h3 className="font-semibold mb-2 text-gray-700">
                    {`${validationResults.missingDomains.length} Clinical Decision${validationResults.missingDomains.length === 1 ? '' : 's'} for Domains ${validationResults.missingDomains.length === 1 ? 'is' : 'are'} missing:`}
                  </h3>
                  <div className="bg-red-50 rounded-lg p-3">
                    {validationResults.missingDomains.map((domain) => (
                      <div key={domain.index} className="text-sm text-red-800 mb-1">
                        <span className="font-medium">{domain.title}:</span>{' '}
                        Decision for at least moderate impairment
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <p className="mb-6 text-sm italic text-amber-700 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <strong>Note:</strong> If you choose to proceed anyway, the
              missing decisions stay unrecorded and will show as Not recorded
              on the review and at the MDT Review.
            </p>

            <div className="flex justify-end gap-3">
              {validationResults.missingSymptoms.length > 0 && (
                <button
                  onClick={navigateToFirstMissingSymptom}
                  className="py-2 px-4 mb-2 font-semibold rounded-md bg-violet-900 text-white hover:bg-violet-800"
                >
                  Go to Symptoms
                </button>
              )}

              {validationResults.missingDomains.length > 0 && (
                <button
                  onClick={navigateToFirstMissingDomain}
                  className="py-2 px-4 mb-2 font-semibold rounded-md bg-violet-900 text-white hover:bg-violet-800"
                >
                  Go to Domains
                </button>
              )}

              <button
                onClick={goToReviewStep}
                className="font-semibold flex items-center py-2 px-4 mb-2 rounded-md shadow-md text-gray-900 border border-gray-300 transition-all duration-300"
              >
                Proceed Anyway
              </button>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </Layout>
  );
}
